import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Snackbar,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AssignmentIcon from "@mui/icons-material/Assignment";

import { AgGridTable } from "../../Component/AgGridTable";
import { EnhancedPageTitle } from "../../Component/PageTitle";
import {
  deleteProject,
  fetchProjects,
  GetCustomers,
  GetPayTerms,
  UpdateProject,
} from "../../services/projectService";
import { Project } from "../../Interface/Project";

const Projects = () => {
  const [projectData, setProjectData] = useState<Project[]>([]);
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [confirmUpdateOpen, setConfirmUpdateOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [payTerms, setPayTerms] = useState<any[]>([]);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error"
  >("success");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    loadProjects();
    loadDropdownData();
  }, []);

  const loadProjects = async () => {
    try {
      const res = await fetchProjects();
      setProjectData(res);
      setAllProjects(res);
    } catch (error) {
      console.error("Error loading projects", error);
    }
  };

  const loadDropdownData = async () => {
    try {
      const [custRes, ptRes] = await Promise.all([GetCustomers(), GetPayTerms()]);
      setCustomers(custRes);
      setPayTerms(ptRes);
    } catch (err) {
      console.error("Error loading dropdowns", err);
    }
  };

 const handleEdit = (project: Project) => {
  const customer = customers.find(c => c.customerName === project.customer);
  const payTerm = payTerms.find(p => p.payTermName === project.payTerms);

  setSelectedProject({
    ...project,
    customerId: customer ? customer.customerId : 0,
    payTermsId: payTerm ? payTerm.payTermId : 0,
    fixed: project.fixed === "Fixed" || project.fixed === true,
    startDate: project.startDate?.split("T")[0] || "",
    endDate: project.endDate?.split("T")[0] || ""
  });

  setEditDialogOpen(true);
};

  const handleDelete = async () => {
    if (!projectToDelete) return;

    try {
      const response = await deleteProject(projectToDelete.projectId);

      if (
        typeof response?.data === "string" &&
        response.data.startsWith("Cannot delete")
      ) {
        throw new Error(response.data);
      }

      setSnackbarMessage("Project deleted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      loadProjects();
    } catch (error: any) {
      console.error("Delete failed:", error);
      const backendMessage =
        error?.response?.data?.data ||
        error?.message ||
        "Project deletion failed.";
      setSnackbarMessage(backendMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setConfirmDialogOpen(false);
      setProjectToDelete(null);
    }
  };
const handleUpdate = async () => {
  if (!selectedProject || !selectedProject.projectId) return;

  const payload = {
    createdOn: selectedProject.createdOn || new Date().toISOString(),
    createdBy: selectedProject.createdBy ?? 0,
    modifiedOn: new Date().toISOString(),
    modifiedBy: 1,
    isDeleted: selectedProject.isDeleted ?? false,
    isActive: selectedProject.isActive ?? true,
    projectId: selectedProject.projectId,
    projectName: selectedProject.projectName,
    customerId: Number(selectedProject.customerId),
    fixed: Boolean(selectedProject.fixed),
    startDate: selectedProject.startDate,
    endDate: selectedProject.endDate,
    payTermsId: Number(selectedProject.payTermsId),
  };

  try {
    setLoading(true);
    await UpdateProject(payload); // PUT request to /UpdateProject
    setEditDialogOpen(false);
    setSelectedProject(null);
    await loadProjects();
    setSnackbarMessage("Project updated successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  } catch (error) {
    console.error("Update failed:", error);
    setSnackbarMessage("Project update failed.");
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
  } finally {
    setLoading(false);
  }
};

  const columns = [
    { field: "projectName", headerName: "Project Name", filter: true },
    { field: "customer", headerName: "Customer", filter: true },
    {
      field: "fixed",
      headerName: "Fixed/ TM",
      valueFormatter: ({ value }: any) => (value ? "Fixed" : "TM"),
      filter: true,
    },
    { field: "startDate", headerName: "Start Date", filter: true },
    { field: "endDate", headerName: "End Date", filter: true },
    { field: "payTerms", headerName: "Pay Terms", filter: true },
    {
      headerName: "Actions",
      field: "actions",
      cellRenderer: (params: any) => {
        const project = params.data;
        return (
          <Stack direction="row" spacing={1}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleEdit(project)}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                setProjectToDelete(project);
                setConfirmDialogOpen(true);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        );
      },
    },
  ];

  return (
    <Box sx={{ padding: 3, backgroundColor: "rgb(238, 242, 246)" }}>
      <EnhancedPageTitle
        title="Project Master"
        icon={<AssignmentIcon />}
        button1Text="Add New"
        button1Action={() => navigate("/AddProject")}
      />

      <AgGridTable
        domLayout="autoHeight"
        customStyles="legacy"
        rowSelection="single"
        gridOptions={{}}
        columns={columns}
        data={projectData}
        handleSelectedRows={() => {}}
        noDataComponentText="No project data available"
        pagination={false}
        paginationPerPage={10}
      />

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Project</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Project Name"
              fullWidth
              value={selectedProject?.projectName || ""}
              onChange={(e) =>
                setSelectedProject((prev) =>
                  prev ? { ...prev, projectName: e.target.value } : prev
                )
              }
            />

            <TextField
              select
              label="Customer"
              value={selectedProject?.customerId || ""}
              onChange={(e) =>
                setSelectedProject((prev) =>
                  prev
                    ? { ...prev, customerId: Number(e.target.value) }
                    : prev
                )
              }
              fullWidth
            >
              {customers.map((cust) => (
                <MenuItem key={cust.customerId} value={cust.customerId}>
                  {cust.customerName}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Fixed/TM"
              value={selectedProject?.fixed ? "Fixed" : "TM"}
              onChange={(e) =>
                setSelectedProject((prev) =>
                  prev ? { ...prev, fixed: e.target.value === "Fixed" } : prev
                )
              }
              fullWidth
            >
              <MenuItem value="Fixed">Fixed</MenuItem>
              <MenuItem value="TM">TM</MenuItem>
            </TextField>

            <TextField
              label="Start Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={selectedProject?.startDate || ""}
              onChange={(e) =>
                setSelectedProject((prev) =>
                  prev ? { ...prev, startDate: e.target.value } : prev
                )
              }
            />

            <TextField
              label="End Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={selectedProject?.endDate || ""}
              onChange={(e) =>
                setSelectedProject((prev) =>
                  prev ? { ...prev, endDate: e.target.value } : prev
                )
              }
            />

            <TextField
              select
              label="Pay Terms"
              value={selectedProject?.payTermsId || ""}
              onChange={(e) =>
                setSelectedProject((prev) =>
                  prev
                    ? { ...prev, payTermsId: Number(e.target.value) }
                    : prev
                )
              }
              fullWidth
            >
              {payTerms.map((pt) => (
                <MenuItem key={pt.tableID} value={pt.tableID}>
                  {pt.tableName}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => setConfirmUpdateOpen(true)}
            variant="contained"
            color="primary"
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Update Dialog */}
      <Dialog
        open={confirmUpdateOpen}
        onClose={() => setConfirmUpdateOpen(false)}
      >
        <DialogTitle>Confirm Update</DialogTitle>
        <DialogContent>
          Are you sure you want to update this project?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUpdateOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmUpdateOpen(false);
              handleUpdate();
            }}
            variant="contained"
            color="primary"
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this project?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default Projects;
